import { useId } from "react";
import { TriangleAlert } from "lucide-react";
import { en, type Translations } from "../../i18n/en";
import { useLanguage } from "../../i18n/language-provider";
import type { PmsProviderCapabilities } from "../../services/pmsApi";
import { pmsDisplayName } from "../drafts/pmsDisplayName";

/**
 * Whether a push of these layers writes a minimum stay beside a price it
 * re-sends: minimum stays on, prices off, to a PMS that keeps the minimum stay
 * on the price. The layers are the ones a send writes, or the ones a restore
 * puts back: either is the same push to the adapter.
 *
 * `rateReadCacheMinutes` is the capability that says so today. It is the
 * switch the server hands such a PMS's adapter Optivo's record of the prices
 * it delivered by, and Better Hotel is the one PMS that carries it: its
 * adapter reads the night's price back and re-sends it beside the minimum
 * stay. A PMS whose price read is live takes a minimum stay as a write of its
 * own (Previo), and nothing is said of one. A PMS nobody has described yet,
 * while the lookup is in flight or after it failed, is not described here
 * either.
 */
export function sendsMinStayBesideResentPrice(
    capabilities: Pick<PmsProviderCapabilities, "rateReadCacheMinutes"> | null | undefined,
    layers: { prices: boolean; minLos: boolean },
): boolean {
    return layers.minLos && !layers.prices && (capabilities?.rateReadCacheMinutes ?? 0) > 0;
}

type MinStayAloneNoticeProps =
    | {
        /** The PMS named in the notice. */
        providerType: string;
        /** A send of the plan's minimum stays: the Send dialog and the auto-sync dialog. */
        variant?: "send";
    }
    | {
        /** The PMS named in the notice. */
        providerType: string;
        /**
         * Saved minimum stays put back: the Upload Log's restore confirmation
         * and its file dialog.
         */
        variant: "restore";
        /**
         * Whether the saved prices could be put back with the minimum stays:
         * the upload being restored wrote prices and its restore point holds
         * them, or the file holds some. A night put back with its saved price
         * is written whatever its price is now, and the notice says so only
         * where the dialog offers that. Required, so that no caller suggests
         * a price its dialog cannot put back.
         */
        pricesOffered: boolean;
    };

/** The notice's words, defaulted field by field: a partial `t` is what some harnesses hand components. */
function minStayAloneCopy(t: Translations | { minStayAlone?: Partial<Translations["minStayAlone"]> }) {
    return { ...en.minStayAlone, ...((t as { minStayAlone?: Partial<Translations["minStayAlone"]> }).minStayAlone ?? {}) };
}

/**
 * Said before minimum stays go without prices to a PMS that keeps the minimum
 * stay on the price ({@link sendsMinStayBesideResentPrice}): beside the
 * minimum-stay switch of the Send dialog and the auto-sync dialog, and in the
 * Upload Log's restore confirmation and its file dialog, which start the same
 * push.
 *
 * Two things happen there that the operator did not ask for and has to hear
 * of first. The night's price is re-sent beside its minimum stay, though no
 * price was to be written. And a night whose price Optivo cannot vouch for is
 * not written: the Upload Log then lists it as held back, which read as a
 * refusal by the PMS until the two were told apart.
 *
 * A restore says it in words of its own, because the send's do not hold
 * there. Its confirmation states its one layer where an upload wrote minimum
 * stays alone, so there is no price switch to be off. A send leaves a night
 * out for a later send, and nothing sends a night a restore left out: it
 * keeps the minimum stay it has. And the prices that could go with the
 * minimum stays are the saved ones, which a restore may not offer
 * (`pricesOffered`), and which would replace the prices in force.
 */
export function MinStayAloneNotice(props: MinStayAloneNoticeProps) {
    const { t } = useLanguage();
    const copy = minStayAloneCopy(t);
    const pms = pmsDisplayName(t, props.providerType);
    const titleId = useId();

    // The same three things, in the order the send says them: what is
    // re-sent, which nights are not written, and what writes a night
    // whatever its price.
    const sentences = props.variant === "restore"
        ? { body: copy.restoreBody, held: copy.restoreHeld, together: props.pricesOffered ? copy.restoreTogether : null }
        : { body: copy.body, held: copy.held, together: copy.together };

    return (
        <div
            role="note"
            aria-labelledby={titleId}
            className="mt-2 space-y-1 rounded-md border border-dashed border-warning/60 bg-warning-surface p-3"
        >
            <p id={titleId} className="flex items-center gap-2 text-sm font-medium text-warning">
                <TriangleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
                {copy.title.replaceAll("{pms}", pms)}
            </p>
            <p className="text-xs text-muted-foreground">{sentences.body.replaceAll("{pms}", pms)}</p>
            <p className="text-xs text-foreground">{sentences.held.replaceAll("{pms}", pms)}</p>
            {sentences.together !== null && (
                <p className="text-xs text-muted-foreground">{sentences.together.replaceAll("{pms}", pms)}</p>
            )}
        </div>
    );
}
